import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Check, X } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { contrastOf, suggestForeground, parseColor, displayRatio } from "./colorUtils";

function Badge({ pass, label }: { pass: boolean; label: string }) {
  return (
    <div
      className={`flex items-center gap-1.5 text-sm ${pass ? "text-green-600" : "text-red-600"}`}
    >
      {pass ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      {label}
    </div>
  );
}

const TOOL_INFO_CONTENT = {
  what: `Checks the contrast ratio between a text color and background color against WCAG accessibility standards, telling you whether the combination is readable enough to meet AA or AAA compliance levels.`,
  how: `Enter or pick your text and background colors, and the contrast ratio and pass/fail status for each accessibility level is calculated instantly, with a live text preview.`,
  tip: `Large text has a lower contrast requirement than normal body text under WCAG guidelines — if a color combination fails for body text but you only need it for a large heading, it may still be compliant.`,
};

export default function ContrastChecker() {
  const [fg, setFg] = useState("#1a1a1a");
  const [bg, setBg] = useState("#ffffff");

  const raw = useMemo(() => contrastOf(fg, bg), [fg, bg]);
  const ratio = raw ?? 0;
  const invalid = raw === null;
  // Floor, don't round: 4.499 must not display as 4.50 next to a failing badge.
  const shown = displayRatio(ratio);
  const suggestion = useMemo(() => suggestForeground(fg, bg), [fg, bg]);
  const pick = (c: string) => parseColor(c)?.toHex().slice(0, 7) ?? "#000000";

  const { blocked } = useLogToolUsage(ratio > 0);

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Text Color</Label>
            <div className="flex gap-2">
              <input
                type="color"
                value={pick(fg)}
                onChange={e => setFg(e.target.value)}
                className="h-10 w-14 rounded border"
              />
              <Input
                value={fg}
                onChange={e => setFg(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Background Color</Label>
            <div className="flex gap-2">
              <input
                type="color"
                value={pick(bg)}
                onChange={e => setBg(e.target.value)}
                className="h-10 w-14 rounded border"
              />
              <Input
                value={bg}
                onChange={e => setBg(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
        </div>
        <div
          className="rounded-lg border flex items-center justify-center p-8 text-lg font-medium"
          style={{ backgroundColor: bg, color: fg }}
        >
          The quick brown fox jumps over the lazy dog
        </div>
      </div>

      <Card className="mt-6 p-6">
        {blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : (
          <>
            <p className="text-3xl font-bold text-center mb-4">
              {invalid ? "—" : `${shown}:1`}
            </p>
            {!invalid && (parseColor(fg)?.alpha() ?? 1) < 1 && <p className="text-xs text-muted-foreground text-center mb-3">The text color is semi-transparent, so it was blended over the background before measuring.</p>}
            {invalid && <p className="text-sm text-destructive text-center mb-3">One of the colors isn't readable. Use HEX, rgb(), hsl() or a color name.</p>}
            <div className="flex justify-center gap-3 mb-4 text-sm">
              <button type="button" className="underline" onClick={() => { setFg(bg); setBg(fg); }}>Swap colors</button>
              {suggestion && <button type="button" className="underline" onClick={() => setFg(suggestion)}>Use {suggestion} (passes AA)</button>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  Normal Text
                </p>
                <Badge pass={ratio >= 4.5} label="AA (4.5:1)" />
                <Badge pass={ratio >= 7} label="AAA (7:1)" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  Large Text
                </p>
                <Badge pass={ratio >= 3} label="AA (3:1)" />
                <Badge pass={ratio >= 4.5} label="AAA (4.5:1)" />
              </div>
              <div className="col-span-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Icons, borders and form controls</p>
                <Badge pass={ratio >= 3} label="WCAG 1.4.11 (3:1)" />
              </div>
            </div>
          </>
        )}
      </Card>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
